import { useState, useEffect } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { FormTokenField, Spinner } from '@wordpress/components';
import apiFetch from '@wordpress/api-fetch';
// import './style.scss';

const PostSearch = ({ selectedPosts = [], onChange, multiple = true, labelText = __('Gösterilecek Yazılar', 'kanews') }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [tokens, setTokens] = useState([]);
    const [mediaUrls, setMediaUrls] = useState({});
    const [postDetails, setPostDetails] = useState({});

    // Seçili gönderilerin detaylarını getir
    useEffect(() => {
        const fetchSelectedPosts = async () => {
            if (!selectedPosts || selectedPosts.length === 0) {
                setTokens([]);
                setPostDetails({});
                return;
            }

            try {
                const response = await apiFetch({
                    path: `/wp/v2/posts?include=${selectedPosts.join(',')}&_fields=id,title,date,featured_media`,
                });
                
                // Post detaylarını sakla
                const newPostDetails = {};
                response.forEach(post => {
                    newPostDetails[post.id] = {
                        id: post.id,
                        title: post.title.rendered || '',
                        date: post.date || '',
                        featured_media: post.featured_media
                    };
                });
                setPostDetails(newPostDetails);
                
                // Token'ları oluştur
                const initialTokens = response.map(post => ({
                    id: post.id,
                    value: post.id,
                    title: post.title.rendered || '',
                    date: post.date || '',
                    featured_media: post.featured_media
                }));
                
                setTokens(initialTokens);
            } catch (error) {
                console.error('Error fetching selected posts:', error);
            }
        };
        
        fetchSelectedPosts();
    }, [selectedPosts]);

    // Medya URL'lerini getir
    useEffect(() => {
        const fetchMediaUrls = async () => {
            const mediaIds = [...new Set([
                ...tokens.map(token => token.featured_media),
                ...suggestions.map(suggestion => suggestion.featured_media)
            ].filter(Boolean))];

            if (mediaIds.length === 0) return;

            try {
                const mediaItems = await Promise.all(
                    mediaIds.map(id => 
                        apiFetch({
                            path: `/wp/v2/media/${id}`,
                        }).catch(() => null)
                    )
                );

                const newMediaUrls = {};
                mediaItems.forEach(item => {
                    if (item && item.id) {
                        newMediaUrls[item.id] = item.source_url;
                    }
                });

                setMediaUrls(prev => ({ ...prev, ...newMediaUrls }));
            } catch (error) {
                console.error('Error fetching media:', error);
            }
        };

        fetchMediaUrls();
    }, [tokens, suggestions]);

    // Arama önerilerini getir
    useEffect(() => {
        if (!searchTerm) {
            setSuggestions([]);
            return;
        }

        const fetchSuggestions = async () => {
            setIsLoading(true);
            try {
                const response = await apiFetch({
                    path: `/wp/v2/posts?search=${encodeURIComponent(searchTerm)}&per_page=10&_fields=id,title,date,featured_media`,
                });
                
                const newSuggestions = response.map(post => ({
                    id: post.id,
                    value: post.id,
                    title: post.title.rendered || '',
                    date: post.date || '',
                    featured_media: post.featured_media
                }));
                
                setSuggestions(newSuggestions);
            } catch (error) {
                console.error('Error fetching suggestions:', error);
                setSuggestions([]);
            }
            setIsLoading(false);
        };

        const debounceTimer = setTimeout(fetchSuggestions, 300);
        return () => clearTimeout(debounceTimer);
    }, [searchTerm]);

    const handleChange = (newTokens) => {
        // Token'ları doğru formata dönüştür
        const formattedTokens = newTokens.map(token => {
            if (typeof token === 'string') {
                // Eğer token bir string ise, mevcut token'lardan veya önerilerden bul
                const existingToken = tokens.find(t => t.title === token) || 
                                    suggestions.find(s => s.title === token);
                return existingToken || { id: token, value: token, title: token };
            }
            return token;
        });
        
        // Multiple false ise sadece son seçilen token'ı al
        const finalTokens = multiple ? formattedTokens : formattedTokens.slice(-1);
        
        setTokens(finalTokens);
        
        // Seçili gönderileri parent bileşene bildir
        const selectedPostsData = finalTokens.map(token => ({
            id: token.id || token.value,
            title: token.title,
            date: token.date,
            featured_media: token.featured_media
        }));
        
        onChange(selectedPostsData);
    };

    const renderSuggestion = (suggestion) => {
        if (!suggestion || typeof suggestion !== 'object') return null;
        
        const mediaUrl = suggestion.featured_media ? mediaUrls[suggestion.featured_media] : null;
        
        return (
            <div className="post-search-suggestion">
                <div className="post-search-suggestion-thumbnail">
                    {mediaUrl ? (
                        <img 
                            src={mediaUrl}
                            alt={suggestion.title || ''} 
                        />
                    ) : (
                        <div className="no-thumbnail"></div>
                    )}
                </div>
                <div className="post-search-suggestion-info">
                    <div className="post-search-suggestion-title">{suggestion.title || ''}</div>
                    <div className="post-search-suggestion-date">
                        {suggestion.date ? new Date(suggestion.date).toLocaleDateString() : ''}
                    </div>
                </div>
            </div>
        );
    };

    // Token'ları string dizisine dönüştür (FormTokenField için)
    const tokenValues = tokens.map(token => token.title);
    const suggestionValues = suggestions.map(suggestion => suggestion.title);

    return (
        <div className="post-search kanews-control-item">
            <FormTokenField
                label={labelText}
                value={tokenValues}
                suggestions={suggestionValues}
                onChange={handleChange}
                onInputChange={setSearchTerm}
                tokenizeOnBlur={true}
                __experimentalExpandOnFocus={true}
                __experimentalAutoSelectFirstMatch={false}
                placeholder={__('Yazı başlığı yazın...', 'kanews')}
                renderSuggestion={renderSuggestion}
                __nextHasNoMarginBottom={true}
                __next40pxDefaultSize={true}
                multiple={multiple}
            />
            {isLoading && <Spinner />}
        </div>
    );
};

export default PostSearch; 